import type { FC, ReactNode } from 'react'

import { Turtle, Volume2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { audioKey, useAudio } from '@/prakrit/lib/client/audio'

interface AudioButtonProps {
    /** Prakrit text to hear. */
    text: string
    className?: string
    /** Also offer slow playback. */
    slow?: boolean
    /** Say plainly that no audio is available, instead of rendering nothing. */
    showMissing?: boolean
    size?: 'sm' | 'md'
}

/**
 * Plays a teacher's recording when one exists, otherwise the device's voice. When neither can play the text,
 * it renders nothing (or an honest note), never a silent button.
 */
export const AudioButton: FC<AudioButtonProps> = ({ text, className, slow = true, showMissing = false, size = 'md' }): ReactNode => {
    const { ready, source, play, playing } = useAudio()
    const key = audioKey(text)

    if (!ready) return null

    const from = source(key)

    if (!from) {
        if (!showMissing) return null

        return (
            <span className={cn('text-xs text-muted-foreground', className)}>
                <span lang='hi' data-ex='hi' className='hindi'>इसकी आवाज़ अभी उपलब्ध नहीं</span>
                <span aria-hidden='true' className='ex-sep'> · </span>
                <span lang='en' data-ex='en'>No audio available yet</span>
            </span>
        )
    }

    const pad = size === 'sm' ? 'h-8 px-2.5 text-xs' : 'min-h-tap px-3.5 text-sm'
    const title = from === 'recording'
        ? 'शिक्षक की रिकॉर्डिंग · Teacher recording'
        : 'उपकरण की आवाज़, लगभग उच्चारण · Device voice, approximate pronunciation'

    return (
        <span className={cn('inline-flex flex-wrap items-center gap-2', className)}>
            <button
                type='button'
                onClick={() => play(key, 'normal')}
                aria-pressed={playing === key}
                title={title}
                className={cn('inline-flex items-center gap-1.5 rounded-full border border-border bg-card font-medium hover:bg-secondary', pad)}
            >
                <Volume2 className='size-4' aria-hidden='true' />
                <span lang='hi' className='hindi'>सुनें</span>
                <span className='sr-only'>{from === 'recording' ? 'Listen to the teacher recording' : 'Listen with the device voice'}</span>
            </button>
            {slow && (
                <button
                    type='button'
                    onClick={() => play(key, 'slow')}
                    title={title}
                    className={cn('inline-flex items-center gap-1.5 rounded-full border border-border bg-card font-medium hover:bg-secondary', pad)}
                >
                    <Turtle className='size-4' aria-hidden='true' />
                    <span lang='hi' className='hindi'>धीरे</span>
                    <span className='sr-only'>Slowly</span>
                </button>
            )}
            {from === 'voice' && size === 'md' && (
                <span className='text-[0.6875rem] text-muted-foreground'>
                    <span lang='hi' className='hindi'>उपकरण की आवाज़</span> · <span lang='en'>device voice</span>
                </span>
            )}
        </span>
    )
}
